import { useContext, useState } from 'react';
import { Fade } from '@progress/kendo-react-animation';

import { Scoring } from './scoring';
import { AnswerProps } from './answer';
import { CustomFeedback } from './custom-feedback';
import {
  ManualScoring,
  TaskContentForm,
  TaskContentAnswer,
  FormCustomFeedback,
  ScoringReductionEntity
} from '../../types/game-document/entities/task-content';
import { QuestionnaireContent } from './questionnaire-content';
import { GameDocumentContext } from '../../contexts/game-document';
import { UploadedImage } from '../../features/game-document/image-resource/resource-window';

export interface QuestionnaireProps extends AnswerProps {
  question?: string;
  mediaOnlyIdentifier?: string;
  taskContentId: string;
  isManualScoring?: boolean;
  isVisibleScoring?: boolean;
  score?: number | undefined;
  isScoringReduction?: boolean;
  manualScoring?: ManualScoring;
  maxAttempts?: number | undefined;
  taskContentForm?: TaskContentForm;
  customFeedback?: FormCustomFeedback;
  pointDepreciation?: number | undefined;
  scoringReduction?: ScoringReductionEntity;
  handleInputChange?: (
    value: string,
    type: 'question' | 'answer' | 'media-only-identifier',
    answerResId?: string
  ) => void;
  onAddMedia?: () => void;
  onCopyForm?: (id: string) => void;
  onDeleteForm?: (id: string) => void;
  onChangeScoring?: (
    score?: number | undefined,
    isManualScoring?: boolean,
    manualScoring?: ManualScoring,
    maxAttempts?: number | undefined,
    pointDepreciation?: number | undefined,
    isScoringReduction?: boolean,
    scoringReduction?: ScoringReductionEntity,
    answersWithScoring?: TaskContentAnswer[]
  ) => void;
  onEditMedia?: (mediaIndex: number) => void;
  onDeleteMedia?: (mediaIndex: number) => void;
  onChangeAnswers?: (answers: TaskContentAnswer[]) => void;
  onCustomFeedbackChange?: (
    customFeedback: FormCustomFeedback,
    uploadedImage: {
      allCorrectUrlResId: UploadedImage | null;
      allWrongUrlResId: UploadedImage | null;
      partiallyCorrectUrlResId: UploadedImage | null;
    }
  ) => void;
}

type TQuestionnaireView = 'questionnaire' | 'scoring' | 'custom-feedback';

export function Questionnaire({
  score,
  question,
  manualScoring,
  taskContentId,
  customFeedback,
  taskContentForm,
  scoringReduction,
  isScoringReduction,
  mediaOnlyIdentifier,
  isManualScoring = false,
  onCopyForm = () => {},
  onAddMedia = () => {},
  onEditMedia = () => {},
  onDeleteForm = () => {},
  onDeleteMedia = () => {},
  onChangeAnswers = () => {},
  onChangeScoring = () => {},
  handleInputChange = () => {},
  onCustomFeedbackChange = () => {},
  ...props
}: QuestionnaireProps) {
  const [state] = useContext(GameDocumentContext);

  const [answerState, setAnswerState] = useState<boolean>(false);
  const [view, setView] = useState<TQuestionnaireView>('questionnaire');

  const onChangeAnswersScoringHandler = (
    score?: number,
    isManualScoring?: boolean,
    manualScoring?: ManualScoring,
    maxAttempts?: number | undefined,
    pointDepreciation?: number | undefined,
    isScoringReduction?: boolean,
    scoringReduction?: ScoringReductionEntity,
    answersWithScoring?: TaskContentAnswer[]
  ) => {
    onChangeScoring(
      score,
      isManualScoring,
      manualScoring,
      maxAttempts,
      pointDepreciation,
      isScoringReduction,
      scoringReduction,
      answersWithScoring
    );
    setView('questionnaire');
  };

  return (
    <>
      {view === 'questionnaire' && (
        <Fade transitionEnterDuration={800} transitionExitDuration={400}>
          <QuestionnaireContent
            question={question}
            mediaOnlyIdentifier={mediaOnlyIdentifier}
            taskContentId={taskContentId}
            onDeleteForm={() => {
              if (props.id !== '') onDeleteForm(props.id!);
            }}
            onCopyForm={() => {
              if (props.id !== '') onCopyForm(props.id!);
            }}
            onShowScoring={() => setView('scoring')}
            onShowCustomFeedback={(id: string, answerType?: string) => {
              setAnswerState(answerType === 'checkbox' ? true : false);
              setView('custom-feedback');
            }}
            onChangeAnswers={(answers: TaskContentAnswer[]) =>
              onChangeAnswers(answers)
            }
            handleInputChange={handleInputChange}
            onAddMedia={onAddMedia}
            taskContentForm={taskContentForm}
            onDeleteMedia={onDeleteMedia}
            onEditMedia={onEditMedia}
            {...props}
          />
        </Fade>
      )}

      {view === 'scoring' && (
        <Fade transitionEnterDuration={800} transitionExitDuration={400}>
          <Scoring
            score={score}
            isManualScoring={isManualScoring}
            manualScoring={manualScoring}
            isScoringReduction={isScoringReduction}
            scoringReduction={scoringReduction}
            onShowScoring={() => setView('questionnaire')}
            onChangeAnswers={onChangeAnswersScoringHandler}
            {...props}
          />
        </Fade>
      )}

      {view === 'custom-feedback' && (
        <Fade transitionEnterDuration={800} transitionExitDuration={400}>
          <CustomFeedback
            taskContentForm={taskContentForm!}
            taskContentId={taskContentId!}
            gameDocument={state.gameDocument!}
            entity={customFeedback}
            onCancel={() => setView('questionnaire')}
            onSubmit={(val, uploadedImage) => {
              onCustomFeedbackChange(val, uploadedImage);
              setView('questionnaire');
            }}
            isShowPartial={answerState}
          />
        </Fade>
      )}
    </>
  );
}
